import React from 'react';
import { Box, Button, ChakraProvider, HStack, Image, Text } from '@chakra-ui/react';

export type BottomNavMode = 'taiao' | 'mapview' | 'maramataka';

export interface BottomNavActionsProps {
  activeMode: string;
  rongoUrl: string;
  onNavigate: (mode: BottomNavMode) => void;
}

interface NavAction {
  key: string;
  label: string;
  markSrc: string;
  mode?: BottomNavMode;
  href?: string;
}

const NAV_ACTIONS: NavAction[] = [
  {
    key: 'map',
    label: 'StreamMAP',
    markSrc: '/assets/icons2/StreamMAP_mark-only_transparent-for-dark.svg',
    mode: 'mapview',
  },
  {
    key: 'dash',
    label: 'StreamDASH',
    markSrc: '/assets/icons2/StreamDASH_mark-only_transparent-for-dark.svg',
    mode: 'taiao',
  },
  {
    key: 'tuhono',
    label: 'Tūhono',
    markSrc: '/assets/icons2/Tuhono_mark-only_transparent-for-dark.svg',
    mode: 'maramataka',
  },
  {
    key: 'rongo',
    label: 'Rongo',
    markSrc: '/assets/icons2/Rongo_mark-only_transparent-for-dark.svg',
  },
];

const getButtonStyles = (isActive: boolean) => ({
  h: '42px',
  minW: 'auto',
  pl: '6px',
  pr: '14px',
  border: '1px solid',
  borderColor: isActive ? '#123c42' : '#dfe8ea',
  borderRadius: '12px',
  bg: isActive ? '#123c42' : '#fff',
  color: isActive ? '#fff' : '#26302b',
  boxShadow: isActive ? '0 2px 7px rgba(18, 48, 56, 0.18)' : '0 1px 2px rgba(18, 48, 56, 0.035)',
  fontFamily: '"PPRightGroteskTextWideBold", Inter, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  letterSpacing: '0.005em',
  whiteSpace: 'nowrap',
  _hover: {
    bg: isActive ? '#123c42' : '#f7fafb',
    borderColor: isActive ? '#123c42' : '#c9d9dd',
  },
  _active: {
    bg: isActive ? '#123c42' : '#f2f7f8',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: '0 0 0 2px #00a7ca',
  },
});

const NavButtonContent: React.FC<{ markSrc: string; label: string; isActive: boolean }> = ({
  markSrc,
  label,
  isActive,
}) => (
  <>
    <Box
      as="span"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w="32px"
      h="32px"
      mr="9px"
      borderRadius="9px"
      bg={isActive ? 'rgba(255, 255, 255, 0.1)' : '#103b40'}
      boxShadow={isActive ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.16)' : 'none'}
    >
      <Image src={markSrc} alt="" display="block" w="auto" h="23px" />
    </Box>
    <Text as="span" fontFamily="inherit" fontSize="14px" fontWeight={400} lineHeight="1" pt="1px">
      {label}
    </Text>
  </>
);

const BottomNavActions: React.FC<BottomNavActionsProps> = ({ activeMode, rongoUrl, onNavigate }) => (
  <ChakraProvider resetCSS={false}>
    <HStack spacing="2" align="center">
      {NAV_ACTIONS.map((action) => {
        const isActive = !!action.mode && action.mode === activeMode;
        const content = <NavButtonContent markSrc={action.markSrc} label={action.label} isActive={isActive} />;

        if (action.key === 'rongo') {
          return (
            <Button
              key={action.key}
              as="a"
              href={rongoUrl}
              target="_blank"
              rel="noopener noreferrer"
              {...getButtonStyles(false)}
            >
              {content}
            </Button>
          );
        }

        return (
          <Button
            key={action.key}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => action.mode && onNavigate(action.mode)}
            {...getButtonStyles(isActive)}
          >
            {content}
          </Button>
        );
      })}
    </HStack>
  </ChakraProvider>
);

export default BottomNavActions;
